/**
 * The layout page of the settings: what is kept for this screen, and the
 * layouts kept by name.
 */

import { useEffect, useState } from 'react';
import {
  applyProfile,
  arrangedHere,
  forgetProfile,
  forgetThisScreen,
  keepProfile,
  LAYOUT_EVENT,
  orientationOf,
  profiles,
  setProfileAuto,
  type LayoutAuto,
} from './layout';

const AUTO_OPTIONS: readonly { value: string; label: string }[] = [
  { value: '', label: 'When chosen' },
  { value: 'wide', label: 'On wide screens' },
  { value: 'tall', label: 'On tall screens' },
];

export function LayoutSettings(): JSX.Element {
  // Read afresh whenever the layout or the screen changes, which is all a
  // redraw here would be for.
  const [, setTick] = useState(0);
  useEffect(() => {
    const again = (): void => setTick((tick) => tick + 1);
    window.addEventListener(LAYOUT_EVENT, again);
    window.addEventListener('resize', again);
    return () => {
      window.removeEventListener(LAYOUT_EVENT, again);
      window.removeEventListener('resize', again);
    };
  }, []);

  const [name, setName] = useState('');
  const kept = profiles();
  const shape = orientationOf();
  const here = arrangedHere();
  const taken = kept.some((one) => one.name === name.trim());

  return (
    <>
      <p className="hint">
        Windows and gauges stay where you put them for each size of screen, and come back there
        whenever the game is that size again. This screen is {window.innerWidth} by{' '}
        {window.innerHeight}, and {shape}.{' '}
        {here
          ? 'It has a layout of its own.'
          : 'Nothing has been arranged at this size yet, so everything keeps its place against the nearest edges of the last layout.'}
      </p>

      {here ? (
        <button type="button" className="wide" onClick={forgetThisScreen}>
          Forget this screen&apos;s layout
        </button>
      ) : null}

      <h4 className="layout-head">Saved layouts</h4>
      {kept.length === 0 ? (
        <p className="hint">None yet. Save the one on the screen now to put it back later.</p>
      ) : (
        kept.map((one) => (
          <div className="layout-row" key={one.name}>
            <span className="layout-name">{one.name}</span>
            <select
              value={one.auto ?? ''}
              aria-label={`When to use ${one.name}`}
              onChange={(event) =>
                setProfileAuto(one.name, (event.target.value || null) as LayoutAuto)
              }
            >
              {AUTO_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <button type="button" onClick={() => applyProfile(one.name)}>
              Use
            </button>
            <button type="button" onClick={() => forgetProfile(one.name)}>
              Delete
            </button>
          </div>
        ))
      )}

      <form
        className="layout-row"
        onSubmit={(event) => {
          event.preventDefault();
          const trimmed = name.trim();
          if (!trimmed) return;
          keepProfile(trimmed);
          setName('');
        }}
      >
        <input
          type="text"
          value={name}
          maxLength={32}
          placeholder="Name this layout"
          onChange={(event) => setName(event.target.value)}
        />
        <button type="submit" disabled={name.trim() === ''}>
          {taken ? 'Replace' : 'Save'}
        </button>
      </form>
      <p className="hint">
        A saved layout set to wide or tall screens is used on any screen of that shape that has no
        layout of its own.
      </p>
    </>
  );
}
